import { CButton, CCard, CCardBody, CCol, CForm, CInputGroup, CRow } from '@coreui/react'
import { Table } from 'antd'
import React, { useState } from 'react'
import * as XLSX from 'xlsx'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import {
  AFFILIATED_TYPE_NAME,
  MEMBER_CATEGORY_DROPDOWN_OPTIONS,
  MEMBER_CATEGORY_NAMES,
} from 'src/models/members'

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'approved', label: 'Approved' },
]

const getStatus = (member) => {
  if (member.approved_by_id) return 'Approved'
  if (member.verified_by_id) return 'Verified'
  return 'Pending'
}

const formatDate = (value) => (value ? String(value).replace('T', ' ').slice(0, 19) : '-')
const valueOrDash = (value) => value ?? '-'

export default function MembersReport() {
  const [isLoading, setIsLoading] = useState(false)
  const [membershipCategory, setMembershipCategory] = useState('')
  const [status, setStatus] = useState('')
  const [reportData, setReportData] = useState([])

  const getReportData = () => {
    setIsLoading(true)
    wtApiCall(
      'reports/members_report',
      { membership_category: membershipCategory, status },
      'get',
      (response) => {
        setReportData(response.data || [])
        setIsLoading(false)
      },
      (error) => {
        setIsLoading(false)
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }

  const exportToExcel = () => {
    const rows = reportData.map((member) => ({
      ID: member.id,
      'Membership Category': MEMBER_CATEGORY_NAMES[member.membership_category] || '-',
      Status: getStatus(member),
      Name: valueOrDash(member.name),
      'S/D/W/O': valueOrDash(member.s_d_w_o),
      'CNIC No.': valueOrDash(member.cnic_no),
      'Shot Gun License No.': valueOrDash(member.shot_gun_license_no),
      '22 Rifle License No.': valueOrDash(member.rifle_22_license_no),
      '9mm Pistol License No.': valueOrDash(member.pistol_9mm_license_no),
      'Shot Gun No.': valueOrDash(member.shot_gun_no),
      '22 Rifle No.': valueOrDash(member.rifle_22_no),
      '9mm Pistol No.': valueOrDash(member.pistol_9mm_no),
      Occupation: valueOrDash(member.occupation),
      Organization: valueOrDash(member.organization),
      Qualification: valueOrDash(member.qualification),
      'Present Address': valueOrDash(member.present_address),
      'Permanent Address': valueOrDash(member.permanent_address),
      'Mobile No.': valueOrDash(member.mobile_phone),
      'Telephone No.': valueOrDash(member.telephone_no),
      Affiliated: AFFILIATED_TYPE_NAME[member.affiliated] || '-',
      Email: valueOrDash(member.email),
      Date: formatDate(member.date),
      'Verified By': valueOrDash(member.verified_by?.name),
      'Verified At': formatDate(member.verified_at),
      'Approved By': valueOrDash(member.approved_by?.name),
      'Approved At': formatDate(member.approved_at),
    }))
    const worksheet = XLSX.utils.json_to_sheet(rows)
    worksheet['!cols'] = Object.keys(rows[0] || {}).map((key) => ({ wch: Math.max(key.length + 2, 16) }))
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Members Report')
    XLSX.writeFile(workbook, 'Members_Report.xlsx')
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', fixed: 'left', width: 80 },
    {
      title: 'Membership Category',
      key: 'membership_category',
      width: 170,
      render: (member) => MEMBER_CATEGORY_NAMES[member.membership_category] || '-',
    },
    { title: 'Status', key: 'status', width: 110, render: getStatus },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 180 },
    { title: 'S/D/W/O', dataIndex: 's_d_w_o', key: 's_d_w_o', width: 160 },
    { title: 'CNIC No.', dataIndex: 'cnic_no', key: 'cnic_no', width: 150 },
    { title: 'Shot Gun License No.', dataIndex: 'shot_gun_license_no', key: 'shot_gun_license_no', width: 180 },
    { title: '22 Rifle License No.', dataIndex: 'rifle_22_license_no', key: 'rifle_22_license_no', width: 180 },
    { title: '9mm Pistol License No.', dataIndex: 'pistol_9mm_license_no', key: 'pistol_9mm_license_no', width: 190 },
    { title: 'Shot Gun No.', dataIndex: 'shot_gun_no', key: 'shot_gun_no', width: 140 },
    { title: '22 Rifle No.', dataIndex: 'rifle_22_no', key: 'rifle_22_no', width: 140 },
    { title: '9mm Pistol No.', dataIndex: 'pistol_9mm_no', key: 'pistol_9mm_no', width: 150 },
    { title: 'Occupation', dataIndex: 'occupation', key: 'occupation', width: 160 },
    { title: 'Organization', dataIndex: 'organization', key: 'organization', width: 160 },
    { title: 'Qualification', dataIndex: 'qualification', key: 'qualification', width: 160 },
    { title: 'Present Address', dataIndex: 'present_address', key: 'present_address', width: 220 },
    { title: 'Permanent Address', dataIndex: 'permanent_address', key: 'permanent_address', width: 220 },
    { title: 'Mobile No.', dataIndex: 'mobile_phone', key: 'mobile_phone', width: 130 },
    { title: 'Telephone No.', dataIndex: 'telephone_no', key: 'telephone_no', width: 130 },
    {
      title: 'Affiliated',
      key: 'affiliated',
      width: 110,
      render: (member) => AFFILIATED_TYPE_NAME[member.affiliated] || '-',
    },
    { title: 'Email', dataIndex: 'email', key: 'email', width: 200 },
    { title: 'Date', key: 'date', width: 150, render: (member) => formatDate(member.date) },
    { title: 'Verified By', key: 'verified_by', width: 160, render: (member) => valueOrDash(member.verified_by?.name) },
    { title: 'Verified At', key: 'verified_at', width: 160, render: (member) => formatDate(member.verified_at) },
    { title: 'Approved By', key: 'approved_by', width: 160, render: (member) => valueOrDash(member.approved_by?.name) },
    { title: 'Approved At', key: 'approved_at', width: 160, render: (member) => formatDate(member.approved_at) },
  ]

  return (
    <div style={{ padding: '2%' }}>
      <h4 className="text-center">Members Report</h4>
      <CRow>
        <CCol md={12}>
          <CCard className="card-dark">
            <CCardBody style={{ backgroundColor: '#f7f9fa' }}>
              <CForm>
                <CRow className="g-3 align-items-end">
                  <CCol md={3}>
                    <CInputGroup>
                      <label>Membership Category</label>
                      <WidgetSelector
                        options={MEMBER_CATEGORY_DROPDOWN_OPTIONS}
                        selectedValue={membershipCategory}
                        placeHolder="All Categories"
                        onChange={(value) => setMembershipCategory(value?.value || '')}
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={3}>
                    <CInputGroup>
                      <label>Status</label>
                      <WidgetSelector
                        options={STATUS_OPTIONS}
                        selectedValue={status}
                        placeHolder="All Statuses"
                        onChange={(value) => setStatus(value?.value || '')}
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={3}>
                    <CButton color="primary" onClick={getReportData} disabled={isLoading}>
                      Search
                    </CButton>
                    <CButton
                      color="success"
                      className="text-white ms-2"
                      onClick={exportToExcel}
                      disabled={isLoading || reportData.length === 0}
                    >
                      Export Excel
                    </CButton>
                  </CCol>
                </CRow>
              </CForm>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow className="mt-4">
        <CCol md={12}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={reportData}
            loading={isLoading}
            pagination={{
              defaultPageSize: 25,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200],
            }}
            scroll={{ x: 4300, y: 'calc(100vh - 360px)' }}
            sticky
          />
        </CCol>
      </CRow>
    </div>
  )
}
